"use client"

import React, {useCallback, useRef, useState} from "react";
import {z} from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {LogOut, MailCheck, Send, TriangleAlert} from "lucide-react";
import {Button} from "@/components/ui/button";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {Loader} from "@/app/components/loader";
import {earlyAccessRequestAPI} from "@/app/apiPaths";
import {AskDotgridIcon} from "@/app/components/chat/AskDotgridIcon";
import {signOut} from "next-auth/react";
import {
  RecaptchaChallenge,
  type RecaptchaChallengeHandle,
} from "@/app/components/onboarding/RecaptchaChallenge";
import {EarlyAccessRequest} from "@/pb/api/devplan/services/web/auth";

const requestSchema = z.object({
  email: z.string().trim().email({message: "Enter a valid email address"}),
  message: z.string().trim().max(2000, "Message must be 2,000 characters or less").optional(),
})

type RequestValues = z.infer<typeof requestSchema>

export interface ClosedAccessProps {
  initialEmail?: string
  resolutionFailed?: boolean
}

export function ClosedAccess({initialEmail = "", resolutionFailed = false}: ClosedAccessProps) {
  return <main className="min-h-screen bg-background text-foreground">
    <div className="mx-auto flex min-h-screen w-full max-w-[680px] flex-col justify-center px-6 py-10">
      <div className="mb-8 flex items-center gap-3">
        <AskDotgridIcon size={26} auto/>
        <div className="text-sm font-medium text-muted-foreground">Devplan</div>
      </div>
      {resolutionFailed && <Alert variant="destructive" className="mb-6">
        <TriangleAlert className="h-4 w-4"/>
        <AlertTitle>Access could not be checked</AlertTitle>
        <AlertDescription>
          Refresh this page to try again. We are keeping product access closed until your workspace, invite, or
          organization status can be verified.
        </AlertDescription>
      </Alert>}
      <div className="space-y-4">
        <h1 className="text-3xl font-semibold tracking-normal">Devplan is not accepting new self-serve signups yet.</h1>
        <p className="text-base leading-7 text-muted-foreground">
          Pre-launch access is limited to existing customers and invited users. To request early access, send a note
          below or contact <a className="font-medium text-foreground underline-offset-4 hover:underline"
                              href="mailto:info@devplan.com">info@devplan.com</a>.
        </p>
      </div>
      <div className="mt-8 rounded-lg border bg-card p-5 shadow-sm">
        <EarlyAccessRequestForm initialEmail={initialEmail}/>
      </div>
      <Button className="mt-4 self-start" type="button" variant="ghost" onClick={() => signOut({callbackUrl: "/"})}>
        <LogOut className="size-4"/>
        Log out
      </Button>
    </div>
  </main>
}

export function EarlyAccessRequestForm({
  initialEmail = "",
}: {
  initialEmail?: string
}) {
  const [processing, setProcessing] = useState(false)
  const [recaptchaReady, setRecaptchaReady] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>()
  const form = useForm<RequestValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: {email: initialEmail, message: ""},
  })
  const recaptchaRef = useRef<RecaptchaChallengeHandle>(null)

  const onSubmit = useCallback(async (values: RequestValues) => {
    setProcessing(true)
    setSubmitError(undefined)
    const recaptchaToken = await recaptchaRef.current?.requireToken("early_access_request")
    if (!recaptchaToken) {
      setProcessing(false)
      return
    }
    try {
      const resp = await fetch(earlyAccessRequestAPI(), {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(EarlyAccessRequest.toJSON({...values, recaptchaToken})),
      })
      if (!resp.ok) {
        if (resp.status === 400 || resp.status === 503) {
          const body = await resp.json().catch(() => undefined) as {error?: unknown} | undefined
          const message = typeof body?.error === "string"
            ? body.error
            : "reCAPTCHA could not be verified. Please try again."
          recaptchaRef.current?.reset(message)
        } else {
          const body = await resp.json().catch(() => undefined) as {error?: unknown} | undefined
          recaptchaRef.current?.reset()
          setSubmitError(typeof body?.error === "string"
            ? body.error
            : "We could not send your request. Please try again.")
        }
        return
      }
      recaptchaRef.current?.reset()
      setSubmitted(true)
    } catch {
      recaptchaRef.current?.reset("Complete reCAPTCHA verification again before retrying.")
      setSubmitError("We could not send your request. Please try again.")
    } finally {
      setProcessing(false)
    }
  }, [])

  if (submitted) {
    return <div className="flex gap-3">
      <MailCheck className="mt-0.5 size-5 text-green-600"/>
      <div>
        <div className="font-medium">Request sent</div>
        <div className="mt-1 text-sm leading-6 text-muted-foreground">
          We received your early-access request and will follow up by email.
        </div>
      </div>
    </div>
  }

  return <Form {...form}>
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <FormField
        control={form.control}
        name="email"
        render={({field}) => <FormItem>
          <FormLabel>Email</FormLabel>
          <FormControl>
            <Input type="email" autoComplete="email" {...field}/>
          </FormControl>
          <FormMessage/>
        </FormItem>}
      />
      <FormField
        control={form.control}
        name="message"
        render={({field}) => <FormItem>
          <FormLabel>Message</FormLabel>
          <FormControl>
            <Textarea rows={5} {...field}/>
          </FormControl>
          <FormMessage/>
        </FormItem>}
      />
      {submitError && <Alert variant="destructive">
        <TriangleAlert className="h-4 w-4"/>
        <AlertTitle>Request failed</AlertTitle>
        <AlertDescription>{submitError}</AlertDescription>
      </Alert>}
      <RecaptchaChallenge ref={recaptchaRef} onReadyChange={setRecaptchaReady}/>
      <Button type="submit" disabled={processing || !recaptchaReady} className="w-full">
        {processing || !recaptchaReady ? <Loader/> : <Send className="size-4"/>}
        Submit request
      </Button>
    </form>
  </Form>
}
